import React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import ReactApexChart from "react-apexcharts";

const RenderChart = ({
  reportData,
  selectedReportType,
  expenseAnalysisRaw,
}) => {
  // Prepare chart data based on report type
  const [chartType, setChartType] = useState("apex"); // 'recharts' or 'apex'

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown Date";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };
  // Prepare chart data based on report type
  const chartData = useMemo(() => {
    try {
      if (!Array.isArray(reportData)) return [];

      switch (selectedReportType) {
        case "stock-level": {
          // Defensive: always return an array
          if (!Array.isArray(reportData)) return [];
          return reportData.slice(0, 10).map((item) => ({
            name: item.item_name || "Unknown",
            value: item.current_quantity || 0,
            category: item.category || "Other",
          }));
        }

        case "inventory-consumption": {
          // Defensive: always return an array
          if (!Array.isArray(reportData)) return [];
          return reportData.slice(0, 10).map((item) => ({
            name: item.item_name || "Unknown",
            consumption: item.total_consumption || 0,
            procurement: item.total_procurement || 0,
            stock: item.current_stock || 0,
          }));
        }

        case "upcoming-bookings": {
          // Group bookings by check-in date and count them
          const bookingsByDate = {};

          reportData?.forEach((item) => {
            const checkInDate =
              item.check_in_date_time || item.check_in_date || "Unknown Date";

            const dateKey = `${checkInDate}`.split(" ")[0];

            if (bookingsByDate[dateKey]) {
              bookingsByDate[dateKey].count += 1;
              bookingsByDate[dateKey].totalAmount += parseFloat(item.total_amount || 0);
              bookingsByDate[dateKey].totalRooms += item.total_rooms || 0;
            } else {
              bookingsByDate[dateKey] = {
                date: dateKey,
                count: 1,
                totalAmount: parseFloat(item.total_amount || 0),
                totalRooms: item.total_rooms || 0,
              };
            }
          });

          // Convert to array and sort by date
          return Object.values(bookingsByDate)
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .slice(0, 15); // Show last 15 days
        }
        case "history-forecast": {
          // Map each entry to a chart-friendly format
          return reportData?.map((item) => ({
            date: item.date,
            actual_bookings: item.actual_bookings || 0,
            forecast_bookings: item.forecast_bookings || 0,
            actual_revenue: item.actual_revenue || 0,
            forecast_revenue: item.forecast_revenue || 0,
          }));
        }
        case "operational-pl": {
          // Map the P&L data to a single object for charting
          return reportData.map((item) => ({
            label: "P&L",
            total_revenue: Number(item.total_revenue) || 0,
            total_expense: Number(item.total_expense) || 0,
            gross_profit: Number(item.gross_profit) || 0,
          }));
        }
        case "expense-analysis": {
          // Use expenseAnalysisRaw (the original API object) for the chart
          let byCategory = [];
          let byDate = [];
          const raw =
            expenseAnalysisRaw ||
            (Array.isArray(reportData) && reportData?.length > 0
              ? reportData[0]
              : {});
          if (raw && raw.expenses_by_category) {
            byCategory = Object.entries(raw.expenses_by_category).map(
              ([category, amount]) => ({ category, amount })
            );
          }
          if (raw && raw.expenses_by_date) {
            byDate = Object.entries(raw.expenses_by_date).map(
              ([date, amount]) => ({ date, amount })
            );
          }
          return { byCategory, byDate };
        }
        case "pos-reports": {
          // Defensive: always return an array
          if (!Array.isArray(reportData)) return [];
          return reportData.map((item) => ({
            item_name: item.item_name || "",
            total_quantity: Number(item.total_quantity) || 0,
            total_value: Number(item.total_value) || 0,
            subcategory_name: item.subcategory_name || "",
          }));
        }
        case "agent-corporate-list": {
          // Always show all three booking types in the pie chart (case-insensitive)
          const types = ["corporate", "group", "individual"];
          const displayNames = {
            corporate: "Corporate",
            group: "Group",
            individual: "Individual",
          };
          const counts = { corporate: 0, group: 0, individual: 0 };
          reportData?.forEach((item) => {
            const type = (item.booking_type || "Unknown").toLowerCase();

            if (counts[type] !== undefined) {
              counts[type] += 1;
            }
          });

          return types.map((type) => ({
            booking_type: displayNames[type],
            count: counts[type],
          }));
        }
        case "inventory-cost-change-report": {
          return (reportData?.length ? reportData : [])?.map((item) => ({
            item_name: item?.item_name || "",
            old_cost: Number(item.old_cost),
            new_cost: Number(item.new_cost),
          }));
        }
        case "budget-variance-alert": {
          // Comprehensive budget variance chart with revenue and expenses
          if (!reportData.length) return [];
          const d = reportData[0];
          return [
            { name: "Total Revenue", value: d.total_revenue },
            { name: "Hotel Revenue", value: d.hotel_revenue },
            { name: "Restaurant Revenue", value: d.restaurant_revenue },
            { name: "Budgeted Expenses", value: d.budgeted_expenses },
            { name: "Actual Expenses", value: d.actual_expenses },
            { name: "Expense Variance", value: d.expense_variance },
            { name: "Actual Profit", value: d.actual_profit },
          ];
        }
        default:
          return [];
      }
    } catch (error) {
      console.log("1", error);
    }
  }, [reportData]);
  console.log(chartData);

  useEffect(() => {
    if (selectedReportType === "agent-corporate-list") {
      setChartType("recharts");
    } else {
      setChartType("apex");
    }
  }, [selectedReportType]);

  // Prepare ApexCharts options
  const getApexChartOptions = () => {
    try {
      switch (selectedReportType) {
        case "stock-level": {
          // Defensive: always use array for categories
          const categories = Array.isArray(chartData)
            ? chartData.map((item) => item.name || "")
            : [];
          return {
            chart: {
              type: "bar",
              height: 400,
            },
            plotOptions: {
              bar: {
                horizontal: false,
                columnWidth: "55%",
              },
            },
            dataLabels: {
              enabled: false,
            },
            xaxis: {
              categories,
              labels: {
                rotate: -45,
              },
            },
            yaxis: {
              title: {
                text: "Stock Level",
              },
            },
            title: {
              text: "Current Stock Levels",
              align: "center",
            },
            colors: ["#008FFB"],
          };
        }

        case "inventory-consumption": {
          // Defensive: always use array for categories
          const categories = Array.isArray(chartData)
            ? chartData.map((item) => item.name || "")
            : [];
          return {
            chart: {
              type: "bar",
              height: 400,
            },
            plotOptions: {
              bar: {
                horizontal: false,
                columnWidth: "55%",
              },
            },
            dataLabels: {
              enabled: false,
            },
            xaxis: {
              categories,
              labels: {
                rotate: -45,
              },
            },
            yaxis: {
              title: {
                text: "Quantity",
              },
            },
            title: {
              text: "Inventory Consumption vs Procurement",
              align: "center",
            },
            colors: ["#FF4560", "#00E396"],
            legend: {
              position: "top",
            },
          };
        }

        case "upcoming-bookings":
          return {
            chart: {
              type: "bar",
              height: 400,
            },
            plotOptions: {
              bar: {
                horizontal: false,
                columnWidth: "55%",
              },
            },
            dataLabels: {
              enabled: true,
            },
            xaxis: {
              categories: (chartData || [])?.map((item) => item.date),
              labels: {
                rotate: -45,
              },
            },
            yaxis: {
              title: {
                text: "Number of Bookings",
              },
            },
            title: {
              text: "Number of Bookings per Day",
              align: "center",
            },
            colors: ["#775DD0"],
            tooltip: {
              y: {
                formatter: (val, { seriesIndex, dataPointIndex, w }) => {
                  const data = chartData[dataPointIndex];
                  return `${val} bookings<br/>Total Rooms: ${
                    data.totalRooms
                  }<br/>Total Amount: ₹${data.totalAmount.toLocaleString()}`;
                },
              },
            },
          };

        case "history-forecast":
          return {
            chart: {
              type: "line",
              height: 400,
            },
            xaxis: {
              categories: (chartData || [])?.map((item) => item.date),
              labels: {
                rotate: -45,
              },
              title: {
                text: "Date",
              },
            },
            yaxis: [
              {
                title: {
                  text: "Bookings",
                },
              },
              {
                opposite: true,
                title: {
                  text: "Revenue",
                },
              },
            ],
            title: {
              text: "Actual vs Forecast Bookings & Revenue",
              align: "center",
            },
            colors: ["#8884d8", "#82ca9d", "#ff7300", "#387908"],
            legend: {
              position: "top",
            },
            tooltip: {
              shared: true,
              intersect: false,
            },
          };
        case "operational-pl":
          return {
            chart: {
              type: "bar",
              height: 400,
            },
            plotOptions: {
              bar: {
                horizontal: false,
                columnWidth: "55%",
              },
            },
            dataLabels: {
              enabled: true,
            },
            xaxis: {
              categories: (chartData || [])?.map((item) => item.label),
              labels: {
                rotate: 0,
              },
            },
            yaxis: {
              title: {
                text: "Amount",
              },
            },
            title: {
              text: "Operational Profit & Loss",
              align: "center",
            },
            colors: ["#008FFB", "#FF4560", "#00E396"],
            legend: {
              position: "top",
            },
            tooltip: {
              y: {
                formatter: (val) => `₹${val}`,
              },
            },
          };
        case "expense-analysis":
          return {
            chart: {
              type: "bar",
              height: 400,
            },
            plotOptions: {
              bar: {
                horizontal: false,
                columnWidth: "55%",
              },
            },
            dataLabels: {
              enabled: true,
            },
            xaxis: {
              categories:
                (chartData || [])?.byCategory?.map((item) => item.category) ||
                [],
              labels: {
                rotate: -45,
              },
              title: {
                text: "Category",
              },
            },
            yaxis: {
              title: {
                text: "Amount",
              },
            },
            title: {
              text: "Expenses by Category",
              align: "center",
            },
            colors: ["#FF4560"],
            legend: {
              position: "top",
            },
          };
        case "pos-reports": {
          // Defensive: always use array for categories
          const categories = Array.isArray(chartData)
            ? chartData.map((item) => item.item_name || "")
            : [];
          return {
            chart: {
              type: "bar",
              height: 400,
            },
            plotOptions: {
              bar: {
                horizontal: false,
                columnWidth: "55%",
              },
            },
            dataLabels: {
              enabled: true,
            },
            xaxis: {
              categories,
              labels: {
                rotate: -45,
              },
              title: {
                text: "Item Name",
              },
            },
            yaxis: {
              title: {
                text: "Quantity / Value",
              },
            },
            title: {
              text: "POS Item Sales (Quantity & Value)",
              align: "center",
            },
            colors: ["#008FFB", "#FF4560"],
            legend: {
              position: "top",
            },
          };
        }
        case "agent-corporate-list":
          return {
            chart: {
              type: "pie",
              height: 400,
            },
            labels: (chartData || [])?.map((item) => item.booking_type),
            title: {
              text: "Booking Types Distribution (Count)",
              align: "center",
            },
            legend: {
              position: "top",
            },
          };
        case "inventory-cost-change-report":
          return {
            chart: {
              type: "bar",
              height: 400,
            },
            plotOptions: {
              bar: {
                horizontal: false,
                columnWidth: "55%",
              },
            },
            dataLabels: {
              enabled: true,
            },
            xaxis: {
              categories: chartData?.length
                ? chartData?.map((item) => item.item_name)
                : [],
              labels: {
                rotate: -45,
              },
              title: {
                text: "Item Name",
              },
            },
            yaxis: {
              title: {
                text: "Cost",
              },
            },
            title: {
              text: "Inventory Cost Change (Old vs New)",
              align: "center",
            },
            colors: ["#008FFB", "#FF4560"],
            legend: {
              position: "top",
            },
          };
        case "budget-variance-alert":
          return {
            chart: {
              type: "bar",
              height: 400,
            },
            plotOptions: {
              bar: {
                horizontal: false,
                columnWidth: "55%",
              },
            },
            dataLabels: {
              enabled: true,
            },
            tooltip: {
              y: {
                formatter: function (value) {
                  return "₹" + value.toLocaleString();
                },
              },
            },
            xaxis: {
              categories: (chartData || [])?.map((item) => item.name),
              labels: {
                rotate: -45,
              },
              title: {
                text: "Type",
              },
            },
            yaxis: {
              title: {
                text: "Amount",
              },
            },
            title: {
              text: "Budget Variance Analysis - Revenue vs Expenses",
              align: "center",
            },
            colors: ["#008FFB", "#FF4560", "#FEB019"],
            legend: {
              position: "top",
            },
          };

        default:
          return {};
      }
    } catch (error) {
      console.log("2", error);
    }
  };

  const getApexChartSeries = () => {
    try {
      switch (selectedReportType) {
        case "stock-level": {
          // Defensive: always use array for data
          const values = Array.isArray(chartData)
            ? chartData.map((item) => item.value || 0)
            : [];
          return [
            {
              name: "Stock Level",
              data: values,
            },
          ];
        }

        case "inventory-consumption": {
          // Defensive: always use array for data
          const consumption = Array.isArray(chartData)
            ? chartData.map((item) => item.consumption || 0)
            : [];
          const procurement = Array.isArray(chartData)
            ? chartData.map((item) => item.procurement || 0)
            : [];
          return [
            {
              name: "Consumption",
              data: consumption,
            },
            {
              name: "Procurement",
              data: procurement,
            },
          ];
        }

        case "upcoming-bookings":
          return [
            {
              name: "Number of Bookings",
              data: chartData?.map((item) => item.count) || [],
            },
          ];

        case "history-forecast":
          return [
            {
              name: "Actual Bookings",
              type: "line",
              data: chartData?.map((item) => Number(item.actual_bookings) || 0),
            },
            {
              name: "Forecast Bookings",
              type: "line",
              data: chartData?.map(
                (item) => Number(item.forecast_bookings) || 0
              ),
            },
            {
              name: "Actual Revenue",
              type: "line",
              data: chartData?.map((item) => Number(item.actual_revenue) || 0),
              yAxis: 1,
            },
            {
              name: "Forecast Revenue",
              type: "line",
              data: chartData?.map(
                (item) => Number(item.forecast_revenue) || 0
              ),
              yAxis: 1,
            },
          ];
        case "operational-pl":
          return [
            {
              name: "Total Revenue",
              data: chartData.map((item) => item.total_revenue),
            },
            {
              name: "Total Expense",
              data: chartData.map((item) => item.total_expense),
            },
            {
              name: "Gross Profit",
              data: chartData.map((item) => item.gross_profit),
            },
          ];
        case "expense-analysis":
          return [
            {
              name: "Amount",
              data: chartData.byCategory?.map((item) => item.amount) || [],
            },
          ];
        case "pos-reports": {
          // Defensive: always use array for data
          const quantity = Array.isArray(chartData)
            ? chartData.map((item) => item.total_quantity || 0)
            : [];
          const value = Array.isArray(chartData)
            ? chartData.map((item) => item.total_value || 0)
            : [];
          return [
            {
              name: "Total Quantity",
              data: quantity,
            },
            {
              name: "Total Value",
              data: value,
            },
          ];
        }
        case "agent-corporate-list":
          return [
            {
              name: "Count",
              data: chartData?.map((item) => item.count),
            },
          ];
        case "inventory-cost-change-report":
          return [
            {
              name: "Old Cost",
              data:
                Array.isArray(chartData) && chartData?.length
                  ? chartData?.map((item) => item.old_cost)
                  : [],
            },
            {
              name: "New Cost",
              data:
                Array.isArray(chartData) && chartData?.length
                  ? chartData?.map((item) => item.new_cost)
                  : [],
            },
          ];
        case "budget-variance-alert":
          return [
            {
              name: "Amount",
              data: chartData?.map((item) => item.value),
            },
          ];

        default:
          return [];
      }
    } catch (error) {
      console.log("3", error);
    }
  };

  const renderRechartsChart = () => {
    try {
      if (!chartData?.length) return null;

      switch (selectedReportType) {
        case "stock-level":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#8884d8" name="Stock Level" />
              </BarChart>
            </ResponsiveContainer>
          );

        case "inventory-consumption":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="consumption" fill="#ff7300" name="Consumption" />
                <Bar dataKey="procurement" fill="#82ca9d" name="Procurement" />
              </BarChart>
            </ResponsiveContainer>
          );

        case "upcoming-bookings":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                key={`upcoming-bookings`}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis />
                <Tooltip
                  formatter={(value, name, props) => [
                    `${value} bookings`,
                    "Number of Bookings",
                  ]}
                  labelFormatter={(label) => `Date: ${label}`}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white p-3 border rounded shadow">
                          <p className="font-semibold">{`Date: ${label}`}</p>
                          <p className="text-blue-600">{`Bookings: ${data.count}`}</p>
                          <p className="text-green-600">{`Total Rooms: ${
                            data?.totalRooms || 0
                          }`}</p>
                          <p className="text-purple-600">{`Total Amount: ₹${data?.totalAmount?.toLocaleString()}`}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar dataKey="count" fill="#8884d8" name="Number of Bookings" />
              </BarChart>
            </ResponsiveContainer>
          );

        case "history-forecast":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData} key="history-forecast">
                <XAxis dataKey="date" />
                <YAxis yAxisId={1} orientation="right" />
                <Tooltip
                  formatter={(value, name) => {
                    if (
                      name === "Actual Revenue" ||
                      name === "Forecast Revenue"
                    ) {
                      return [`₹${value.toLocaleString()}`, name];
                    }
                    return [value, name];
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="actual_bookings"
                  stroke="#8884d8"
                  name="Actual Bookings"
                  yAxisId={1}
                />
                <Line
                  type="monotone"
                  dataKey="forecast_bookings"
                  stroke="#82ca9d"
                  name="Forecast Bookings"
                  yAxisId={1}
                />
                <Line
                  type="monotone"
                  dataKey="actual_revenue"
                  stroke="#ff7300"
                  name="Actual Revenue"
                  yAxisId={1}
                />
                <Line
                  type="monotone"
                  dataKey="forecast_revenue"
                  stroke="#387908"
                  name="Forecast Revenue"
                  yAxisId={1}
                />
              </LineChart>
            </ResponsiveContainer>
          );
        case "operational-pl":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip formatter={(value) => `₹${value}`} />
                <Legend />
                <Bar
                  dataKey="total_revenue"
                  fill="#008FFB"
                  name="Total Revenue"
                />
                <Bar
                  dataKey="total_expense"
                  fill="#FF4560"
                  name="Total Expense"
                />
                <Bar
                  dataKey="gross_profit"
                  fill="#00E396"
                  name="Gross Profit"
                />
              </BarChart>
            </ResponsiveContainer>
          );
        case "expense-analysis":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={chartData.byCategory}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="category"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis />
                <Tooltip formatter={(value) => `₹${value}`} />
                <Legend />
                <Bar dataKey="amount" fill="#FF4560" name="Amount" />
              </BarChart>
            </ResponsiveContainer>
          );
        case "pos-reports":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="item_name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis />
                <Tooltip formatter={(value) => `${value}`} />
                <Legend />
                <Bar
                  dataKey="total_quantity"
                  fill="#008FFB"
                  name="Total Quantity"
                />
                <Bar dataKey="total_value" fill="#FF4560" name="Total Value" />
              </BarChart>
            </ResponsiveContainer>
          );
        case "agent-corporate-list":
          // Pie chart for Recharts
          const nonZeroTypes = chartData?.filter((d) => d.count > 0);
          return (
            <ResponsiveContainer width="100%" height={400}>
              <PieChart key="agent-corporate-list">
                <Pie
                  data={chartData}
                  dataKey="count"
                  nameKey="booking_type"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  label={
                    nonZeroTypes.length > 1
                      ? ({ booking_type, percent }) =>
                          `${booking_type}: ${parseFloat(percent * 100).toFixed(
                            0
                          )}%`
                      : false
                  }
                  fill="#008FFB"
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        ["#008FFB", "#FF4560", "#00E396", "#775DD0", "#FEB019"][
                          index % 5
                        ]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name, props) => [
                    `${value} reservations`,
                    name,
                  ]}
                />
                {<Legend />}
              </PieChart>
            </ResponsiveContainer>
          );
        case "inventory-cost-change-report":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="item_name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis />
                <Tooltip formatter={(value) => `${value}`} />
                <Legend />
                <Bar dataKey="old_cost" fill="#008FFB" name="Old Cost" />
                <Bar dataKey="new_cost" fill="#FF4560" name="New Cost" />
              </BarChart>
            </ResponsiveContainer>
          );
        case "budget-variance-alert":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis />
                <Tooltip formatter={(value) => `${value}`} />
                <Legend />
                <Bar dataKey="value" fill="#008FFB" name="Amount" />
              </BarChart>
            </ResponsiveContainer>
          );

        default:
          return null;
      }
    } catch (error) {
      console.log("3", error);
    }
  };
  return (
    <div>
      {/* <div className="me-3">
        <select
          className="form-select"
          value={chartType}
          onChange={(e) => setChartType(e.target.value)}
          style={{ width: "auto" }}
        >
          <option value="recharts">Recharts</option>
          <option value="apex">ApexCharts</option>
        </select>
      </div> */}
      {/* Defensive check for ApexCharts data */}
      {chartType === "apex" &&
        (() => {
          const apexSeries = getApexChartSeries();
          const apexOptions = getApexChartOptions();
          const isApexDataValid =
            Array.isArray(apexSeries) &&
            apexSeries.length > 0 &&
            apexSeries.every(
              (s) =>
                Array.isArray(s.data) &&
                s.data.length > 0 &&
                s.data.every((v) => typeof v === "number" && !isNaN(v))
            ) &&
            apexOptions &&
            apexOptions.xaxis &&
            Array.isArray(apexOptions.xaxis.categories) &&
            apexOptions.xaxis.categories.length > 0;
          if (!isApexDataValid) {
            return (
              <div className="alert alert-warning">
                No data available for this chart.
              </div>
            );
          }
          return null;
        })()}
      {/* 🎯 CHART PLACEMENT SECTION - This is where your graph goes! */}
      {reportData?.length > 0 && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              {selectedReportType === "stock-level" && "Current Stock Levels"}
              {selectedReportType === "inventory-consumption" &&
                "Inventory Consumption vs Procurement"}
              {selectedReportType === "upcoming-bookings" &&
                "Booking Amounts Trend"}
              {selectedReportType === "history-forecast" &&
                "Actual vs Forecast Bookings & Revenue"}
              {selectedReportType === "operational-pl" &&
                "Operational Profit & Loss"}
              {selectedReportType === "expense-analysis" &&
                "Expense Analysis (by Category)"}
              {selectedReportType === "pos-reports" &&
                "POS Item Sales (Quantity & Value)"}
              {selectedReportType === "agent-corporate-list" &&
                "Booking Types Distribution (Count)"}
              {selectedReportType === "inventory-cost-change-report" &&
                "Inventory Cost Change (Old vs New)"}
              {selectedReportType === "budget-variance-alert" &&
                "Budget Variance Alert"}
            </h5>
            <small className="text-muted">
              Using {chartType === "recharts" ? "Recharts" : "ApexCharts"}
            </small>
          </div>
          <div className="card-body">
            {selectedReportType === "upcoming-bookings" &&
            (chartData?.length === 0 || !chartData[0].date) ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "400px" }}
              >
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="mt-2 text-muted">
                    Loading upcoming bookings data...
                  </div>
                </div>
              </div>
            ) : chartType === "recharts" ? (
              <div key={`recharts-${selectedReportType}`}>
                {renderRechartsChart()}
              </div>
            ) : (
              (() => {
                const apexSeries = getApexChartSeries();
                const apexOptions = getApexChartOptions();
                const isApexDataValid =
                  Array.isArray(apexSeries) &&
                  apexSeries.length > 0 &&
                  apexSeries.every(
                    (s) =>
                      Array.isArray(s.data) &&
                      s.data.length > 0 &&
                      s.data.every((v) => typeof v === "number" && !isNaN(v))
                  ) &&
                  apexOptions &&
                  apexOptions.xaxis &&
                  Array.isArray(apexOptions.xaxis.categories) &&
                  apexOptions.xaxis.categories.length > 0;
                if (!isApexDataValid) {
                  return null;
                }
                return (
                  <ReactApexChart
                    key={`apex-${selectedReportType}`}
                    options={apexOptions}
                    series={apexSeries}
                    type={apexOptions.chart?.type}
                    height={400}
                  />
                );
              })()
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RenderChart;
